import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  AmountType,
  CurveType,
  DriverType,
  ForecastMethodType,
  GqlService,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventTrackerService } from '@shared/store/event/event-tracker.service';
import { Utils } from '@shared/utils/utils';

export interface ForecastSettings {
  applyInvestigator: boolean;
  applyPassThrough: boolean;
  applyServices: boolean;
  customCurve: null | string;
  customEndMilestone: null | string;
  customStartMilestone: null | string;
  driver: 'patient' | 'site' | 'time';
  endMilestone: Milestone | null;
  milestone: Milestone | null;
  milestoneCategory: null | string;
  patientCurve: null | string;
  siteCurve: null | string;
  siteDriverPeriod: CurveType | null;
  startMilestone: Milestone | null;
  timeMethod: ForecastMethodType | null;
}

type Milestone = {
  categoryId: string;
  categoryName: string;
  contractEndDate: string;
  contractEndDateId: string;
  contractStartDate: string;
  contractStartDateId: string;
  id: string;
  name: string;
};

@Injectable()
export class QuickForecastService {
  private readonly eventService = inject(EventTrackerService);
  private readonly gqlService = inject(GqlService);
  private readonly overlayService = inject(OverlayService);

  private readonly mapSaveMethodByDriver = new Map<
    'custom' | 'patient' | 'site' | 'time',
    (
      p: ForecastSettings,
      transactionId: string,
      trackId: string,
      vendorIds: string[]
    ) => Promise<GraphqlResponse<unknown>>
  >([
    [
      'custom',
      (p, t, trackId, vendorIds) => this.saveCustomForecastSettings(p, t, trackId, vendorIds),
    ],
    [
      'patient',
      (p, t, trackId, vendorIds) => this.savePatientForecastSettings(p, t, trackId, vendorIds),
    ],
    ['site', (p, t, trackId, vendorIds) => this.saveSiteForecastSettings(p, t, trackId, vendorIds)],
    ['time', (p, t, trackId, vendorIds) => this.saveTimeForecastSettings(p, t, trackId, vendorIds)],
  ]);

  private getAmountType({
    applyInvestigator,
    applyPassThrough,
    applyServices,
  }: ForecastSettings): AmountType[] {
    const amountTypes = [];

    if (applyServices) {
      amountTypes.push(AmountType.AMOUNT_SERVICE);
    }

    if (applyPassThrough) {
      amountTypes.push(AmountType.AMOUNT_PASSTHROUGH);
    }

    if (applyInvestigator) {
      amountTypes.push(AmountType.AMOUNT_INVESTIGATOR);
    }

    return amountTypes;
  }

  private saveCustomForecastSettings(
    params: ForecastSettings,
    transactionId: string,
    trackId: string,
    vendorIds: string[]
  ) {
    return firstValueFrom(
      this.gqlService.bulkUpdateBudgetForecastSettings$({
        amount_types: this.getAmountType(params),
        driver: DriverType.DRIVER_CUSTOM,
        driver_setting_id: params.customCurve,
        forecast_method: null,
        milestone_category: null,
        period_end_date: null,
        period_end_milestone_id: null,
        period_start_date: null,
        period_start_milestone_id: null,
        tracking_id: trackId,
        transaction_id: transactionId,
        vendor_ids: vendorIds,
      })
    );
  }

  private savePatientForecastSettings(
    params: ForecastSettings,
    transactionId: string,
    trackId: string,
    vendorIds: string[]
  ) {
    return firstValueFrom(
      this.gqlService.bulkUpdateBudgetForecastSettings$({
        amount_types: this.getAmountType(params),
        driver: DriverType.DRIVER_PATIENT,
        driver_setting_id: params.patientCurve,
        forecast_method: null,
        milestone_category: null,
        period_end_date: null,
        period_end_milestone_id: null,
        period_start_date: null,
        period_start_milestone_id: null,
        tracking_id: trackId,
        transaction_id: transactionId,
        vendor_ids: vendorIds,
      })
    );
  }

  private saveSiteForecastSettings(
    params: ForecastSettings,
    transactionId: string,
    trackId: string,
    vendorIds: string[]
  ) {
    return firstValueFrom(
      this.gqlService.bulkUpdateBudgetForecastSettings$({
        amount_types: this.getAmountType(params),
        driver: DriverType.DRIVER_SITE,
        driver_setting_id: params.siteCurve,
        forecast_method: null,
        milestone_category: null,
        period_end_date: null,
        period_end_milestone_id: null,
        period_start_date: null,
        period_start_milestone_id: null,
        tracking_id: trackId,
        transaction_id: transactionId,
        vendor_ids: vendorIds,
      })
    );
  }

  private saveTimeForecastSettings(
    params: ForecastSettings,
    transactionId: string,
    trackId: string,
    vendorIds: string[]
  ) {
    return firstValueFrom(
      this.gqlService.bulkUpdateBudgetForecastSettings$({
        amount_types: this.getAmountType(params),
        driver: DriverType.DRIVER_TIME,
        driver_setting_id: null,
        forecast_method: params.timeMethod,
        milestone_category: params.milestoneCategory,
        period_end_date: params.customEndMilestone || null,
        period_end_milestone_id: params.milestone?.id
          ? params.milestone.contractEndDateId
          : params.endMilestone?.id || null,
        period_start_date: params.customStartMilestone || null,
        period_start_milestone_id: params.milestone?.id
          ? params.milestone.contractStartDateId
          : params.startMilestone?.id || null,
        tracking_id: trackId,
        transaction_id: transactionId,
        vendor_ids: vendorIds,
      })
    );
  }

  async saveUpdateBudgetForecastSettings(params: ForecastSettings, vendorIds: string[]) {
    const { driver } = params;

    const method = this.mapSaveMethodByDriver.get(driver);

    if (method) {
      const transactionId = Utils.uuid();
      const trackId = Utils.uuid();
      const { errors, success } = await method(params, transactionId, trackId, vendorIds);

      if (errors.length) {
        this.overlayService.error(errors);
      }

      if (success) {
        void this.eventService.trackEvent(trackId, false, true);
      }

      return success;
    }

    return false;
  }
}
